import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { CommissionType, CommissionTierRequest, RestaurantCommissionConfig } from '../../types/restaurants';

interface RestaurantCommissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (config: {
    commissionType: CommissionType;
    fixedPercentage?: number;
    tiers?: CommissionTierRequest[];
  }) => void;
  onDelete?: () => void;
  currentConfig: RestaurantCommissionConfig | null;
  globalDefaultPercentage: number;
  loading?: boolean;
  deleting?: boolean;
}

interface TierFormData {
  orderThreshold: string;
  percentage: string;
}

const RestaurantCommissionModal: React.FC<RestaurantCommissionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  currentConfig,
  globalDefaultPercentage,
  loading = false,
  deleting = false
}) => {
  const { t } = useTranslation();
  const [commissionType, setCommissionType] = useState<'GLOBAL' | CommissionType>('GLOBAL');
  const [fixedPercentage, setFixedPercentage] = useState('');
  const [tiers, setTiers] = useState<TierFormData[]>([{ orderThreshold: '', percentage: '' }]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      if (currentConfig) {
        setCommissionType(currentConfig.commissionType);
        if (currentConfig.commissionType === CommissionType.FIXED) {
          setFixedPercentage(currentConfig.fixedPercentage?.toString() || '');
        } else if (currentConfig.commissionType === CommissionType.TIERED) {
          setTiers(currentConfig.tiers.map(tier => ({
            orderThreshold: tier.orderThreshold.toString(),
            percentage: tier.percentage.toString()
          })));
        }
      } else {
        // No config - use global default
        setCommissionType('GLOBAL');
        setFixedPercentage('');
        setTiers([{ orderThreshold: '', percentage: '' }]);
      }
      setErrors({});
      setShowDeleteConfirm(false);
    }
  }, [isOpen, currentConfig]);

  const handleClose = () => {
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleCommissionTypeChange = (type: 'GLOBAL' | CommissionType) => {
    setCommissionType(type);
    setErrors({});

    // Reset form data when switching types
    if (type === CommissionType.FIXED && !fixedPercentage) {
      setFixedPercentage('');
    } else if (type === CommissionType.TIERED && tiers.length === 0) {
      setTiers([{ orderThreshold: '', percentage: '' }]);
    }
  };

  const handleFixedPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFixedPercentage(value);
      if (errors.fixedPercentage) {
        setErrors({ ...errors, fixedPercentage: '' });
      }
    }
  };

  const handleTierChange = (index: number, field: 'orderThreshold' | 'percentage', value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const newTiers = [...tiers];
      newTiers[index][field] = value;
      setTiers(newTiers);

      // Clear errors for this tier
      if (errors[`tier${index}_${field}`]) {
        const newErrors = { ...errors };
        delete newErrors[`tier${index}_${field}`];
        setErrors(newErrors);
      }
    }
  };

  const addTier = () => {
    setTiers([...tiers, { orderThreshold: '', percentage: '' }]);
  };

  const removeTier = (index: number) => {
    if (tiers.length > 1) {
      const newTiers = tiers.filter((_, i) => i !== index);
      setTiers(newTiers);

      // Clear errors for this tier
      const newErrors = { ...errors };
      delete newErrors[`tier${index}_orderThreshold`];
      delete newErrors[`tier${index}_percentage`];
      setErrors(newErrors);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (commissionType === CommissionType.FIXED) {
      const percentage = parseFloat(fixedPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        newErrors.fixedPercentage = t('modals.configureRestaurantCommission.validationFixedPercentage');
      }
    } else if (commissionType === CommissionType.TIERED) {
      if (tiers.length === 0) {
        newErrors.tiers = t('modals.configureRestaurantCommission.validationTiersEmpty');
      }

      tiers.forEach((tier, index) => {
        const threshold = parseInt(tier.orderThreshold);
        const percentage = parseFloat(tier.percentage);

        if (isNaN(threshold) || threshold <= 0) {
          newErrors[`tier${index}_orderThreshold`] = t('modals.configureRestaurantCommission.validationThreshold');
        }

        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          newErrors[`tier${index}_percentage`] = t('modals.configureRestaurantCommission.validationTierPercentage');
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (commissionType === 'GLOBAL') {
      // Delete configuration - use global default
      if (onDelete && currentConfig) {
        onDelete();
      } else {
        // Nothing to do - just close
        handleClose();
      }
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (commissionType === CommissionType.FIXED) {
      onSubmit({
        commissionType: CommissionType.FIXED,
        fixedPercentage: parseFloat(fixedPercentage)
      });
    } else if (commissionType === CommissionType.TIERED) {
      onSubmit({
        commissionType: CommissionType.TIERED,
        tiers: tiers.map(tier => ({
          orderThreshold: parseInt(tier.orderThreshold),
          percentage: parseFloat(tier.percentage)
        }))
      });
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const isFormValid = () => {
    if (commissionType === 'GLOBAL') {
      return currentConfig !== null; // Can only switch to global if there's a config to delete
    }

    if (commissionType === CommissionType.FIXED) {
      const percentage = parseFloat(fixedPercentage);
      return !isNaN(percentage) && percentage >= 0 && percentage <= 100;
    }

    if (commissionType === CommissionType.TIERED) {
      return tiers.length > 0 && tiers.every(tier => {
        const threshold = parseInt(tier.orderThreshold);
        const percentage = parseFloat(tier.percentage);
        return !isNaN(threshold) && threshold > 0 && !isNaN(percentage) && percentage >= 0 && percentage <= 100;
      });
    }

    return false;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('modals.configureRestaurantCommission.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        {/* Global default info box */}
        <div className="modal-info-box" style={{
          padding: '0.75rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          {t('modals.configureRestaurantCommission.globalDefaultInfo', { percentage: globalDefaultPercentage })}
        </div>

        {/* Commission Type Selection */}
        <div className="modal-form-group">
          <label>{t('modals.configureRestaurantCommission.commissionType')}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="commissionType"
                value="GLOBAL"
                checked={commissionType === 'GLOBAL'}
                onChange={() => handleCommissionTypeChange('GLOBAL')}
                disabled={loading || deleting}
                style={{ marginRight: '0.5rem' }}
              />
              {t('modals.configureRestaurantCommission.useGlobalDefault')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="commissionType"
                value={CommissionType.FIXED}
                checked={commissionType === CommissionType.FIXED}
                onChange={() => handleCommissionTypeChange(CommissionType.FIXED)}
                disabled={loading || deleting}
                style={{ marginRight: '0.5rem' }}
              />
              {t('modals.configureRestaurantCommission.fixedType')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="commissionType"
                value={CommissionType.TIERED}
                checked={commissionType === CommissionType.TIERED}
                onChange={() => handleCommissionTypeChange(CommissionType.TIERED)}
                disabled={loading || deleting}
                style={{ marginRight: '0.5rem' }}
              />
              {t('modals.configureRestaurantCommission.tieredType')}
            </label>
          </div>
        </div>

        {/* Fixed Percentage Configuration */}
        {commissionType === CommissionType.FIXED && (
          <div className="modal-form-group">
            <label htmlFor="fixedPercentage">{t('modals.configureRestaurantCommission.fixedPercentage')}</label>
            <input
              type="text"
              id="fixedPercentage"
              value={fixedPercentage}
              onChange={handleFixedPercentageChange}
              placeholder={t('modals.configureRestaurantCommission.fixedPercentagePlaceholder')}
              required
              disabled={loading || deleting}
            />
            <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
              {t('modals.configureRestaurantCommission.fixedPercentageHint')}
            </small>
            {errors.fixedPercentage && (
              <small style={{ color: '#dc3545', marginTop: '0.25rem', display: 'block' }}>
                {errors.fixedPercentage}
              </small>
            )}
          </div>
        )}

        {/* Tiered Configuration */}
        {commissionType === CommissionType.TIERED && (
          <div>
            <div className="modal-form-group">
              <label>{t('modals.configureRestaurantCommission.tieredConfiguration')}</label>
              <small style={{ color: '#666', marginBottom: '1rem', display: 'block' }}>
                {t('modals.configureRestaurantCommission.tiersExplanation')}
              </small>
            </div>

            {tiers.map((tier, index) => (
              <div key={index} style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.75rem'
                }}>
                  <strong>{t('modals.configureRestaurantCommission.tierLabel', { number: index + 1 })}</strong>
                  {tiers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTier(index)}
                      disabled={loading || deleting}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.85rem',
                        color: '#dc3545',
                        backgroundColor: 'transparent',
                        border: '1px solid #dc3545',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {t('modals.configureRestaurantCommission.removeTier')}
                    </button>
                  )}
                </div>

                <div className="modal-form-group">
                  <label htmlFor={`threshold-${index}`}>
                    {t('modals.configureRestaurantCommission.orderThreshold')}
                  </label>
                  <input
                    type="text"
                    id={`threshold-${index}`}
                    value={tier.orderThreshold}
                    onChange={(e) => handleTierChange(index, 'orderThreshold', e.target.value)}
                    placeholder={t('modals.configureRestaurantCommission.orderThresholdPlaceholder')}
                    required
                    disabled={loading || deleting}
                  />
                  {errors[`tier${index}_orderThreshold`] && (
                    <small style={{ color: '#dc3545', marginTop: '0.25rem', display: 'block' }}>
                      {errors[`tier${index}_orderThreshold`]}
                    </small>
                  )}
                </div>

                <div className="modal-form-group">
                  <label htmlFor={`percentage-${index}`}>
                    {t('modals.configureRestaurantCommission.percentage')}
                  </label>
                  <input
                    type="text"
                    id={`percentage-${index}`}
                    value={tier.percentage}
                    onChange={(e) => handleTierChange(index, 'percentage', e.target.value)}
                    placeholder={t('modals.configureRestaurantCommission.percentagePlaceholder')}
                    required
                    disabled={loading || deleting}
                  />
                  {errors[`tier${index}_percentage`] && (
                    <small style={{ color: '#dc3545', marginTop: '0.25rem', display: 'block' }}>
                      {errors[`tier${index}_percentage`]}
                    </small>
                  )}
                </div>

                <div style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  padding: '0.5rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  {t('modals.configureRestaurantCommission.tierLogicLabel', {
                    threshold: tier.orderThreshold || '?'
                  })} → {t('modals.configureRestaurantCommission.tierCommission', {
                      percentage: tier.percentage || '?'
                    })}
                </div>
              </div>
            ))}

            {errors.tiers && (
              <small style={{ color: '#dc3545', marginBottom: '1rem', display: 'block' }}>
                {errors.tiers}
              </small>
            )}

            <button
              type="button"
              onClick={addTier}
              disabled={loading || deleting}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              + {t('modals.configureRestaurantCommission.addTier')}
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={loading || deleting}
          >
            {t('modals.configureRestaurantCommission.cancel')}
          </button>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {currentConfig && onDelete && !showDeleteConfirm && commissionType !== 'GLOBAL' && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="modal-button danger"
                disabled={loading || deleting}
              >
                {t('modals.configureRestaurantCommission.deleteConfiguration')}
              </button>
            )}

            {showDeleteConfirm && (
              <button
                type="button"
                onClick={handleDelete}
                className="modal-button danger"
                disabled={loading || deleting}
              >
                {deleting
                  ? t('modals.configureRestaurantCommission.deleting')
                  : t('modals.configureRestaurantCommission.deleteConfirm')}
              </button>
            )}

            <button
              type="submit"
              className="modal-button primary"
              disabled={loading || deleting || !isFormValid()}
            >
              {loading
                ? t('modals.configureRestaurantCommission.saving')
                : t('modals.configureRestaurantCommission.save')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default RestaurantCommissionModal;
